import { env } from "~/env";

/**
 * Whether to route the prompts page at all.
 *
 * A prompt override replaces what a model is told to do with no review behind it, so gen-ai
 * only serves the facility on a PR app labelled `enable gen-ai prompt overrides`. Site.ts
 * bakes the same decision into `APP_GEN_AI_PROMPT_OVERRIDES`, which is never set for dev,
 * staging, demo or prod.
 *
 * Deliberately not `env.APP_SHOW_DEV_VIEW`, the app's usual "non-production" switch. That
 * is true on `dev` and on every PR app, and gating on it would render this page against an
 * API that refuses every call.
 *
 * The build flag names the stage the bundle was built for, which is not the same question as
 * which API it talks to. Running the dev server against a PR app's gateway — `pnpm dev:dev`
 * with `FEDERATION_GATEWAY_URL` repointed — leaves it built for `dev` while every request goes
 * somewhere that answers perfectly well, and the page would be hidden for no reason.
 *
 * So a dev server always gets the page: whoever is running one is, by definition, developing.
 * If the gateway they happen to be pointed at does refuse, the page says so plainly rather
 * than being missing, which is the more useful of the two failures. Nothing is loosened for a
 * deployed stage — `localhost` is never a hostname a real one is served from.
 */
export const promptOverridesAvailable = () =>
  env.APP_GEN_AI_PROMPT_OVERRIDES || isLocalDevServer();

const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "[::1]", "0.0.0.0"];

const isLocalDevServer = () =>
  typeof window !== "undefined" &&
  LOCAL_HOSTNAMES.includes(window.location.hostname);
